import { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Divider from '@mui/material/Divider';
import Chip, { ChipProps } from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Tooltip from '@mui/material/Tooltip';
import { CSSObject } from '@mui/material/styles';

import {
  useGetOrdersQuery,
  useCreateBulkOrderMutation,
} from '#services/order';
import {
  formatDate,
  formatCurrency,
} from '#common/utils';
import {
  FULFILLMENT_LITERAL,
  FULFILLMENT_COLOR,
  FULFILLMENT_STATUS,
  BULK_ORDER_FILTER_LITERAL,
} from '#common/constants';
import {
  PAYMENT_COLOR,
} from '#common/constants/literal-map';
import URLSearchParamsEx from '#utils/url-ex';
import EnhancedTable, { EnhancedTableProps } from '#components/EnhancedTable';
import {
  DEFAULT_TIME_RANGE_PRESET,
  TimeRangeValue,
  resolveDateRangeParam,
  toDateRangeQueryParams,
} from '#components/time-range.utils';
import DropdownFilterBox from '#components/DropdownFilterBox';
import RecencyDateText from '#components/RecencyDateText';
import { FeedbackUIContext } from '#context/feedback-ui-context';

import {
  ReminderChip,
  getReminderMeta,
} from './reminder.utils';

import {
  setDateRange,
  setFilter,
  setFilterKey,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setSelected,
  setSkipFlag,
} from './order.slice';

interface OrderListSlice {
  dateRange: TimeRangeValue;
  filter: string;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  pageSize: number;
  rows: EnhancedTableProps['rows'];
  rowCount: number;
  selected: Array<string | number | undefined>;
  skip: boolean;
}

interface OrderRow {
  id?: string | number;
  createdAsUserId?: string | number;
  createdAsUserName?: string;
  createdAsUserCode?: string | number;
  createdDate?: string;
  nextEstimatedShipDate?: string;
  shippingReminderStatus?: string;
  pendingItemCount?: number;
  earliestConfirmDeadline?: string | null;
  linkedBulkOrderIds?: Array<string | number>;
  items?: Array<{ fromInventory?: boolean; amounts?: number; [key: string]: unknown }>;
  fulfillmentStatus?: number;
  totalPrice?: number;
  shipableItemCount?: number;
  tags?: string[];
  receivings?: Array<string | number>;
  [key: string]: unknown;
}

export default function AdminOrders() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const userInfo = useSelector(
    (state: { auth: { userInfo?: { isAdmin?: boolean } | null } }) => state.auth.userInfo,
  );
  const timeZone = useSelector((state: { appConfig: { timeZone?: string } }) => state.appConfig.timeZone);
  const filterKey = useSelector((state: { orderList: OrderListSlice }) => state.orderList.filterKey);
  const filter = useSelector((state: { orderList: OrderListSlice }) => state.orderList.filter);
  const dateRange = useSelector((state: { orderList: OrderListSlice }) => state.orderList.dateRange);
  const headerFilters = useSelector((state: { orderList: OrderListSlice }) => state.orderList.headerFilters);
  const order = useSelector((state: { orderList: OrderListSlice }) => state.orderList.order);
  const orderBy = useSelector((state: { orderList: OrderListSlice }) => state.orderList.orderBy);
  const pageIndex = useSelector((state: { orderList: OrderListSlice }) => state.orderList.pageIndex);
  const pageSize = useSelector((state: { orderList: OrderListSlice }) => state.orderList.pageSize);
  const rows = useSelector((state: { orderList: OrderListSlice }) => state.orderList.rows);
  const rowCount = useSelector((state: { orderList: OrderListSlice }) => state.orderList.rowCount);
  const selected = useSelector((state: { orderList: OrderListSlice }) => state.orderList.selected);
  const [createBulkOrder] = useCreateBulkOrderMutation();
  const [debouncedPageIndex] = useDebounce(pageIndex, 300);
  const [filterForQuery, setFilterForQuery] = useState(filter);
  const skip = useSelector((state: { orderList: OrderListSlice }) => state.orderList.skip);
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Restore state from URL parameters on component mount only
  useEffect(() => {
    if (!isInitialMount) return;

    const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    if (urlParams.values.filter !== undefined) {
      dispatch(setFilter(urlParams.values.filter as string));
      setFilterForQuery(urlParams.values.filter as string);
    }
    if (urlParams.values.filterKey !== undefined) {
      dispatch(setFilterKey(urlParams.values.filterKey as string));
    }
    if (urlParams.values.headerFilters !== undefined) {
      dispatch(setHeaderFilters(urlParams.values.headerFilters as Record<string, Record<string, boolean>>));
    }
    if (urlParams.values.order !== undefined) {
      dispatch(setOrder(urlParams.values.order as string));
    }
    if (urlParams.values.orderBy !== undefined) {
      dispatch(setOrderBy(urlParams.values.orderBy as string));
    }
    if (urlParams.values.pageSize !== undefined) {
      dispatch(setPageSize(urlParams.values.pageSize as number));
    }

    setIsInitialMount(false);
  }, [location.search, isInitialMount, dispatch]);

  // Sync filterForQuery with filter when filter changes
  useEffect(() => {
    setFilterForQuery(filter);
  }, [filter]);

  const {
    showBackdrop,
    hideBackdrop,

    showModal,
    hideModal,
    setModalData,

    showDialog,
    hideDialog,
    setDialogData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const dateRangeParam = resolveDateRangeParam('createdDate', dateRange);

  const query = useGetOrdersQuery({
    order,
    orderBy,
    filter: filterForQuery,
    filterKey,
    headerFilters,
    pageIndex: debouncedPageIndex,
    pageSize,
    dateRange: dateRangeParam,
  }, {
    refetchOnMountOrArgChange: true,
    skip,
  });
  const { refetch: refetchOrders } = query;

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  useEffect(() => {
    // When filterForQuery changes, always trigger a search by setting skip to false
    dispatch(setSkipFlag(false));
  }, [filterForQuery, dispatch]);

  const buildOrderDetailUrl = (orderId?: string | number, orderIndex?: number) => {
    const params: Record<string, unknown> = {
      order,
      orderBy,
      filter,
      filterKey,
      headerFilters,
      pageSize,
      orderId,
    };
    if (orderIndex !== undefined) {
      params.orderIndex = orderIndex;
      // The detail page re-runs the list query to resolve orderIndex (and to
      // walk prev/next), so it needs the same date window the list used —
      // without it the index points into the unfiltered list.
      if (dateRangeParam) {
        params.dateRange = dateRangeParam;
      }
    }
    const qs = new URLSearchParamsEx(params);
    return `/management/order/detail?${qs.toString()}`;
  };

  const createBulkDisabled = !userInfo?.isAdmin
    || !selected.length
    || selected.every((s) => {
      const find = rows.find((r) => r.id === s) as OrderRow | undefined;
      if (!find) {
        // ignore the order id from the other pages
        return false;
      }
      return find.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED;
    });

  const handleOrdersExport = () => {
    // WORKAROUND react state cannot work, use local variable instead
    let exportTargetOption = 'current';

    setModalData({
      children: (
        <>
          <Typography variant="h6" gutterBottom>
            Export Orders
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControl sx={{ mb: 5 }}>
            <FormLabel id="label-export-radio">Export</FormLabel>
            <RadioGroup
              aria-labelledby="label-export-radio"
              defaultValue="current"
              name="radio-buttons-group"
              onChange={(event, value) => {
                exportTargetOption = value;
              }}
            >
              <FormControlLabel value="current" control={<Radio />} label="Current page" />
              <FormControlLabel value="all" control={<Radio />} label="All orders" />
              <FormControlLabel
                disabled={!selected.length}
                value="selected"
                control={<Radio />}
                label={`Selected: ${selected.length} orders`}
              />
            </RadioGroup>
          </FormControl>

          <Stack spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={hideModal}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                let additionalParam = '';
                if (exportTargetOption === 'current') {
                  additionalParam = `order=${order}&orderBy=${orderBy}&filter=${filter}&page=${pageIndex}&pageSize=${pageSize}`;
                } else if (exportTargetOption === 'all') {
                  additionalParam = `order=${order}&orderBy=${orderBy}&filter=${filter}&all=1`;
                } else if (exportTargetOption === 'selected') {
                  additionalParam = `orderIds=${selected.join(',')}`;
                }

                // An explicit selection is already the exact row set the user
                // picked, so only the page/all exports carry the table's range.
                if (exportTargetOption !== 'selected') {
                  const rangeParams = new URLSearchParams(
                    toDateRangeQueryParams(dateRangeParam),
                  ).toString();
                  if (rangeParams) {
                    additionalParam = `${additionalParam}&${rangeParams}`;
                  }
                }

                const exportUrl = `/api/export/orders?${additionalParam}`;
                window.open(exportUrl, '_blank');
                hideModal();
              }}
            >
              Export orders
            </Button>
          </Stack>
        </>
      ),
      style: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
      },
    });
    showModal();
  };

  const handleBulkOrder = () => {
    setDialogData({
      title: 'Confirmation',
      text: `Convert selected ${selected.length} orders into bulk orders?`,
      agree: 'Yes',
      disagree: 'No',
      onDisagree: () => hideDialog(),
      onAgree: () => {
        hideDialog();
        showBackdrop();

        void createBulkOrder({
          orderIds: selected,
        }).then((res) => {
          const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], 'Bulk order was successfully created.');
          if (!hasError) {
            void refetchOrders();
          }
        }).finally(() => {
          hideBackdrop();
          dispatch(setSelected(selected));
        });
      },
    });
    showDialog();
  };

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'id',
    label: 'Order #',
    renderer: (row, idx) => {
      const orderRow = row as OrderRow;
      const orderIndex = pageIndex * pageSize + idx + 1;
      const url = buildOrderDetailUrl(orderRow.id, orderIndex);
      return (
        <Link
          href={url}
          onClick={(e) => {
            e.preventDefault();
            void navigate(url);
          }}
          sx={{ textDecoration: 'inherit' }}
        >
          {orderRow.id}
        </Link>
      );
    },
  }, {
    id: 'createdAsUserName',
    label: 'Store Name',
    renderer: (row) => {
      const orderRow = row as OrderRow;
      const url = `/management/user/detail?userId=${orderRow.createdAsUserId}`;
      return (
        <Link
          href={url}
          onClick={(e) => {
            e.preventDefault();
            void navigate(url);
          }}
          sx={{ textDecoration: 'inherit' }}
        >
          {orderRow.createdAsUserName}
          #
          {orderRow.createdAsUserCode}
        </Link>
      );
    },
  }, {
    id: 'createdDate',
    label: 'Order Date',
    renderer: (o) => <RecencyDateText date={(o as OrderRow).createdDate} />,
  }, {
    id: 'shippingReminderDays',
    label: (
      <Tooltip title="Shows the earliest estimated ship date across items in the order, so reminders focus on the most urgent ETA.">
        <span>Ship ETA</span>
      </Tooltip>
    ),
    renderer: (o) => {
      const orderRow = o as OrderRow;
      if (!orderRow.nextEstimatedShipDate && !orderRow.shippingReminderStatus) {
        return '-';
      }
      return (
        <Stack spacing={0.5}>
          <Typography variant="body2">
            {orderRow.nextEstimatedShipDate ? formatDate(orderRow.nextEstimatedShipDate) : '—'}
          </Typography>
          <ReminderChip row={orderRow} />
        </Stack>
      );
    },
  }, {
    id: 'pendingItems',
    label: (
      <Tooltip title="Customized items still waiting for a ship date, with the earliest confirmation deadline.">
        <span>Pending Items</span>
      </Tooltip>
    ),
    sortable: false,
    filters: { 1: 'Has pending items' },
    renderer: (o) => {
      const orderRow = o as OrderRow;
      if (!orderRow.pendingItemCount) {
        return '-';
      }
      return (
        <Stack spacing={0.5}>
          <Typography variant="body2">
            {`${orderRow.pendingItemCount} pending`}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {orderRow.earliestConfirmDeadline
              ? `Due ${formatDate(orderRow.earliestConfirmDeadline, '', { timeZone })}`
              : 'No deadline'}
          </Typography>
        </Stack>
      );
    },
  }, {
    id: 'bulkOrder',
    label: 'Bulk Order',
    sortable: false,
    filters: BULK_ORDER_FILTER_LITERAL,
    renderer: (o) => {
      const orderRow = o as OrderRow;
      const { linkedBulkOrderIds } = orderRow;

      const allFromInventory = (orderRow.items || []).every((itm) => itm.fromInventory);
      if (allFromInventory) {
        return <Chip label="Inventory Order" color="warning" size="small" />;
      }

      if (!linkedBulkOrderIds) {
        return null;
      }
      return linkedBulkOrderIds.map((bulkOrderId) => {
        const url = `/management/bulk/detail?bulkOrderId=${bulkOrderId}`;
        return (
          <Link
            key={bulkOrderId}
            href={url}
            onClick={(e) => {
              e.preventDefault();
              void navigate(url);
            }}
            sx={{
              ml: 0.5,
              textDecoration: 'inherit',
            }}
          >
            {bulkOrderId}
          </Link>
        );
      });
    },
  }, {
    id: 'fulfillmentStatus',
    label: 'Fulfillment Status',
    filters: FULFILLMENT_LITERAL,
    renderer: (o) => {
      const status = (o as OrderRow).fulfillmentStatus as number;
      return (
        <Chip
          size="small"
          label={FULFILLMENT_LITERAL[status]}
          color={FULFILLMENT_COLOR[status] as ChipProps['color']}
        />
      );
    },
  }, {
    id: 'totalPrice',
    label: 'Total',
    renderer: (o) => {
      const orderRow = o as OrderRow;
      return (orderRow.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED)
        ? `${formatCurrency(0)}`
        : `${formatCurrency(orderRow.totalPrice as unknown as Parameters<typeof formatCurrency>[0])}`;
    },
  }, {
    id: 'shipableItemCount',
    label: 'Shipping',
    renderer: (o) => {
      const orderRow = o as OrderRow;
      if (orderRow.shipableItemCount
        && orderRow.fulfillmentStatus !== FULFILLMENT_STATUS.CANCELED) {
        return (
          <Chip
            size="small"
            label={`New In Warehouse (${orderRow.shipableItemCount})`}
            color={PAYMENT_COLOR[2] as ChipProps['color']}
          />
        );
      }
      return '-';
    },
  }, {
    id: 'tags',
    label: 'Tags',
    renderer: (o) => ((o as OrderRow).tags || []).join(','),
  }, {
    id: 'itemCount',
    label: 'Items',
    renderer: (o) => {
      const orderRow = o as OrderRow;
      return (orderRow.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED)
        ? 0
        : (orderRow.items || []).reduce((total, itm) => total + Number(itm.amounts), 0);
    },
  // }, {
  //   id: 'paymentStatus',
  //   label: 'Payment Status',
  //   renderer: (o) => (
  //     <Chip
  //       size="small"
  //       label={PAYMENT_STATUS[o.paymentStatus]}
  //       color={PAYMENT_COLOR[o.paymentStatus]}
  //     />
  //   ),
  }, {
    id: 'receivings',
    label: 'Receiving List',
    sortable: false,
    renderer: (o) => {
      const { receivings } = o as OrderRow;
      return (receivings || []).map((receivingId) => {
        const url = `/management/receiving/edit?receivingId=${receivingId}`;
        return (
          <Link
            key={receivingId}
            href={url}
            onClick={(e) => {
              e.preventDefault();
              void navigate(url);
            }}
            sx={{
              ml: 0.5,
              textDecoration: 'inherit',
            }}
          >
            {receivingId}
          </Link>
        );
      });
    },
  }];

  const tableProps: EnhancedTableProps = {
    name: 'order-list-admin',

    headers: tableHeaders,

    pinnedColumns: {
      left: ['id'],
    },

    enableSort: true,
    order,
    setOrder,
    orderBy,
    setOrderBy,

    headerFilters,
    setHeaderFilters,

    getRowStyle: (row) => {
      const orderRow = row as OrderRow;
      const style: CSSObject = {};
      if (orderRow.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED) {
        style.textDecoration = 'line-through';
        style.opacity = 0.6;
      }
      const reminderMeta = getReminderMeta(orderRow);
      if (reminderMeta?.backgroundColor) {
        style.backgroundColor = reminderMeta.backgroundColor;
      }
      return style;
    },

    hasSelectColumn: true,
    // Pending customized items can't go into a bulk order until their ship date is confirmed.
    canSelect: (row) => !(row as OrderRow | undefined)?.pendingItemCount,
    canSelectAll: () => true,
    selectDisabledTooltip: (row) => `Order ${String(row.id)} contains customized items awaiting confirmation. Ship dates have not yet been confirmed.`,
    selected,
    setSelected,

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,

    timeRange: {
      value: dateRange,
      onChange: (value) => dispatch(setDateRange(value)),
      columnLabel: 'Order Date',
      defaultPreset: DEFAULT_TIME_RANGE_PRESET,
      entityLabel: 'orders',
    },

    rows,
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Customer Orders
      </Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Order Quick Actions
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              Quickly access key order management features with a single click.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => { void navigate('/management/order/overdue'); }}
            >
              View Overdue Orders
            </Button>
            <Button
              variant="contained"
              onClick={() => { void navigate('/management/order/reminders'); }}
            >
              View Order Reminders
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <DropdownFilterBox
          width={500}
          dropdownItems={[{
            value: 'orderId',
            text: 'Order #',
          }, {
            value: 'poNumber',
            text: 'PO #',
          }, {
            value: 'style',
            text: 'Style #',
          }, {
            value: 'inventoryOrderId',
            text: 'Inventory #',
          }, {
            value: 'barcode',
            text: 'Barcode #',
          }, {
            value: 'storeName',
            text: 'Store Name',
          }]}
          filter={filter}
          filterKey={filterKey}
          filterPlaceholder="Filter orders"
          dispatch={dispatch as (action: unknown) => void}
          setFilter={setFilter}
          setFilterKey={setFilterKey}
          setFilterForQuery={setFilterForQuery}
        />

        <ButtonGroup variant="outlined" size="small">
          <Button
            disabled={createBulkDisabled}
            onClick={handleBulkOrder}
          >
            Create Bulk Order
          </Button>

          <Button
            onClick={handleOrdersExport}
          >
            Export
          </Button>
        </ButtonGroup>
      </Box>
      <EnhancedTable
        {...tableProps}
      />
    </>
  );
}
